"use client";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/* ─── Types ─────────────────────────────────────────────────── */
type Value = {
  title: string;
  body: string;
};

/* ─── Scroll-reveal hook ────────────────────────────────────── */
function useVisible(threshold = 0.1) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  return [ref, visible] as const;
}

const delayClassMap = {
  0: "delay-0",
  10: "delay-100",
  15: "delay-150",
  20: "delay-200",
  30: "delay-300",
} as const;

function getDelayClass(delay: number) {
  return delayClassMap[Math.round(delay * 100) as keyof typeof delayClassMap] ?? "delay-0";
}

function Reveal({
  children,
  delay = 0,
  from = "bottom",
}: {
  children: React.ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, visible] = useVisible();

  const fromClasses = {
    bottom: "translate-y-8",
    left: "-translate-x-8",
    right: "translate-x-8",
  } as const;

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0",
        fromClasses[from],
        "transition-all duration-[650ms] ease-out",
        getDelayClass(delay),
        visible && "opacity-100 translate-x-0 translate-y-0"
      )}
    >
      {children}
    </div>
  );
}

const VALUES: Value[] = [
  {
    title: "Integrity",
    body: "Complete honesty and ethical responsibility in every engagement. Transparency is the foundation of our client relationships — we never overpromise.",
  },
  {
    title: "Excellence",
    body: "High-quality, reliable, scalable solutions through continuous improvement and relentless focus on performance that creates lasting value.",
  },
  {
    title: "Collaboration",
    body: "The best results come from working with clients, not simply for them. Your knowledge combined with our expertise builds stronger strategies.",
  },
  {
    title: "Innovation",
    body: "We embrace emerging technologies and challenge conventional thinking to deliver forward-looking solutions in a rapidly evolving landscape.",
  },
  {
    title: "Empowerment",
    body: "Equipping businesses with the knowledge, clarity and tools to succeed independently — enabling confident decisions and sustainable growth.",
  },
  {
    title: "Client-First",
    body: "Our clients' goals define our strategy. We prioritize their success above all else, building partnerships rooted in trust and accountability.",
  },
];

const STATS = [
  { v: "2024", l: "Year Established" },
  { v: "100+", l: "Clients Served" },
  { v: "50+", l: "Professionals" },
  { v: "30+", l: "Services" },
  { v: "1,000+", l: "Certifications" },
  { v: "₹50Cr+", l: "Funds Facilitated" },
  { v: "100+", l: "IT Projects" },
];

function Divider({ className }: { className?: string }) {
  return <div className={cn("w-12 h-[3px] bg-[#1565C0] mb-7", className)} />;
}



function ValueItem({ v, idx }: { v: Value; idx: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-start gap-5 py-7 transition-all duration-500 ease-out",
        idx < 5 ? "border-b border-[#E5E7EB]" : "",
        delayClasses[idx],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 mt-[2px] rounded-full bg-[#1565C0]">
        <span className="text-[12px] font-semibold tracking-[0.04em] text-white">{String(idx + 1).padStart(2, "0")}</span>
      </div>
      <div>
        <div className="text-[15px] font-semibold text-[#111827] mb-1.5">{v.title}</div>
        <p className="text-[14px] leading-[1.85] text-[#6B7280]">{v.body}</p>
      </div>
    </div>
  );
}

function StatCard({ s, i }: { s: { v: string; l: string }; i: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300", "delay-300", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "text-center px-3 py-7 transition-all duration-500 ease-out xl:border-r xl:last:border-r-0",
        delayClasses[i],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )}
    >
      <div className="text-xl sm:text-2xl lg:text-[30px] font-black text-[#1565C0] leading-none">{s.v}</div>
      <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-[#6B7280] mt-2">{s.l}</div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <main className="bg-white">
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[450px]">
        <div className="flex flex-col justify-center bg-white py-10 px-4 sm:px-8 lg:pl-20 lg:pr-16">
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">Who We Are</div>
          <Divider />
          <h1 className="text-[clamp(28px,3.5vw,46px)] font-extrabold text-[#111827] leading-[1.13] mb-6 ">
            Your Strategic Partner for Business Growth and Digital Transformation
          </h1>
          <p className="text-[16px] leading-[1.85] text-[#6B7280] mb-10 ">
            We exist to fill the gap between ambition and execution — combining deep business expertise with cutting-edge technology to help Indian entrepreneurs and MSMEs grow with confidence.
          </p>
          <div className="flex flex-wrap gap-3">
            <button className="inline-flex items-center justify-center rounded-sm bg-[#1565C0] text-white px-7 py-3 text-[13px] font-bold tracking-[0.06em]">
              MEET THE TEAM
            </button>
            <button className="inline-flex items-center justify-center rounded-sm border border-[#E5E7EB] bg-transparent text-[#111827] px-7 py-3 text-[13px] font-semibold tracking-[0.06em]">
              OUR STORY
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[350px] lg:min-h-[450px]">
          <Image
            src="/images/about/about.png"
            alt="Farsight office"
            fill
            className="object-cover"
          />
        </div>
      </section>

      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { v: "100+", l: "Clients Served" },
          { v: "50+", l: "Professionals" },
          { v: "30+", l: "Services" },
          { v: "₹50Cr+", l: "Funds Facilitated" },
        ].map((s, i) => (
          <div key={i} className={cn("py-5 text-center", i < 3 ? "lg:border-r border-white/15" : "")}> 
            <div className="text-lg sm:text-xl lg:text-[26px] font-black text-white leading-none">{s.v}</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/70 mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">
          <Reveal from="left">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">Our Story</div>
            <Divider />
            <h2 className="text-[clamp(26px,3.2vw,40px)] font-bold text-[#111827] leading-[1.18] mb-5">
              More Than a Consultancy — A Growth Engine
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#6B7280] mb-4">
              Farsight Solutions LLP is a trusted strategic partner to entrepreneurs, startups and MSMEs across India. We combine business consulting expertise with advanced IT capabilities to help organizations at every level — from incorporation and compliance to growth strategy, digital transformation and financing readiness.
            </p>
            <p className="text-[15px] leading-[1.85] text-[#6B7280] mb-4">
              Our mission is simple: make complex business processes easier, introduce transparency at every step and deliver measurable results that matter.
            </p>
            <p className="text-[15px] leading-[1.85] text-[#6B7280]">
              Headquartered in India, we serve national clients across manufacturing, retail, healthcare, education and finance. Our IT solutions extend to global clients, helping organizations scale and innovate in an increasingly digital world.
            </p>
          </Reveal>

          <Reveal from="right" delay={0.15}>
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[380px]">
              <Image
                src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&q=80"
                alt="Company building"
                fill
                className="object-cover"
              />
            </div>
            <div className="absolute -bottom-9 -right-7 w-1/2 h-[190px] border-4 border-white shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden">
              <Image
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=500&q=80"
                alt="Team collaboration"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F9FAFB]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="text-center mb-10 lg:mb-16">
              <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">Our Foundation</div>
              <Divider className="mx-auto mb-0" />
              <h2 className="text-[clamp(26px,3.2vw,40px)] font-bold text-[#111827] leading-[1.18] text-center">Vision. Mission. Purpose.</h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 ">
            {[
              {
                label: "Vision",
                body: "To be the most trusted business partner for organizations worldwide — where innovative business concepts become registered ventures, complex processes become efficient systems and ambitions become sustainable, future-ready businesses.",
                img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80",
              },
              {
                label: "Mission",
                body: "To enable businesses to register, stay compliant, access funding and scale with clarity — by delivering smart, transparent and results-oriented consultancy and technology solutions.",
                img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&q=80",
              },
              {
                label: "Purpose",
                body: "We exist to eliminate the hurdles that slow business growth — delays in registration, complexities in compliance, or limitations in technology — so companies can focus on building, growing and fulfilling their vision.",
                img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80",
              },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-white overflow-hidden shadow-md min-h-[450px] ">
                  <div className="relative w-full h-[200px]">
                    <Image src={item.img} alt={item.label} fill className="object-cover" />
                  </div>
                  <div className="p-6 pb-[28px]">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-2.5">{item.label}</div>
                    <p className="text-[14px] leading-[1.85] text-[#6B7280]">{item.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)] gap-8 lg:gap-20">
          <Reveal from="left">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">What Guides Us</div>
            <Divider />
            <h2 className="text-[clamp(26px,3.2vw,40px)] font-bold text-[#111827] leading-[1.18] mb-7">Our Core Values</h2>
            <p className="text-[15px] leading-[1.85] text-[#6B7280] mb-9">
              Our values shape every decision we make, every solution we deliver and every relationship we build. They are the operating principles of our firm — not words on a wall.
            </p>
            <div className="relative w-full h-[200px] sm:h-[250px] lg:h-[300px]">
              <Image
                src="https://images.unsplash.com/photo-1553484771-371a605b060b?w=700&q=80"
                alt="Core values"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <div className="pt-1">
            {VALUES.map((value, index) => (
              <ValueItem key={index} v={value} idx={index} />
            ))}
          </div>
        </div>
      </section>

      {/* <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F9FAFB]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">The People Behind Farsight</div>
              <Divider />
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <h2 className="text-[clamp(26px,3.2vw,40px)] font-bold text-[#111827] leading-[1.18]">Experts Committed to Your Success</h2>
                <p className="text-[14px] leading-[1.85] text-[#6B7280] max-w-[340px] text-right">
                  Specialists across strategy, law, finance and technology — united by one goal.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {TEAM.map((member, index) => (
              <TeamCard key={index} member={member} delay={index * 0.07} />
            ))}
          </div>
        </div>
      </section> */}

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="text-center mb-10 lg:mb-16">
              <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">Farsight at a Glance</div>
              <Divider />
              <h2 className="text-[clamp(26px,3.2vw,40px)] font-bold text-[#111827] leading-[1.18]">By the Numbers</h2>
            </div>
          </Reveal>

          <Reveal>
            <div className="relative w-full h-[180px] sm:h-[240px] lg:h-[300px] mb-8 lg:mb-12">
              <Image
                src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1400&q=80"
                alt="Farsight workspace"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7">
            {STATS.map((stat, index) => (
              <StatCard key={index} s={stat} i={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F9FAFB] py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">
          <Reveal from="left">
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[380px]">
              <Image
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80"
                alt="Get started"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">Ready to Grow?</div>
            <Divider />
            <h2 className="text-[clamp(26px,3.2vw,40px)] font-bold text-[#111827] leading-[1.18] mb-6">
              Let&apos;s Build Your Success Story
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#6B7280] mb-9">
              Join 100+ businesses that trust Farsight Solutions for integrated business and technology services — from registration to digital transformation and beyond.
            </p>
            <div className="flex flex-wrap gap-3">
              <button className="inline-flex items-center justify-center rounded-sm bg-[#1565C0] text-white px-8 py-3 text-[13px] font-bold tracking-[0.06em]">
                GET STARTED TODAY
              </button>
              <button className="inline-flex items-center justify-center rounded-sm border border-[#E5E7EB] bg-transparent text-[#111827] px-8 py-3 text-[13px] font-semibold tracking-[0.06em]">
                CONTACT US
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
